import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { staffApi } from "./staffApi";
import { Paginated, Receivable } from "../types/dtos";

export type ReceivableListParams = { page?: number; pageSize?: number; q?: string; status?: string };

async function fetchReceivables(params: ReceivableListParams): Promise<Paginated<Receivable>> {
  const res = await staffApi.get("/receivables", { params });
  return res.data;
}

export function useReceivables(params: ReceivableListParams): UseQueryResult<Paginated<Receivable>> {
  return useQuery({
    queryKey: ["receivables", params],
    queryFn: () => fetchReceivables(params),
    placeholderData: (prev: Paginated<Receivable> | undefined) => prev,
  });
}

export type ReceivableFormInput = {
  customerId: number;
  contractId?: number;
  reference: string;
  dueDate: string;
  value: number;
};

export function useCreateReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceivableFormInput) => staffApi.post("/receivables", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["receivables"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export type ReceivableUpdateInput = Partial<Pick<ReceivableFormInput, "reference" | "dueDate" | "value">>;

export function useUpdateReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ReceivableUpdateInput }) => staffApi.patch(`/receivables/${id}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["receivables"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useBaixaReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, settlementTypeId, paidAt }: { id: number; settlementTypeId: number; paidAt: string }) =>
      staffApi.post(`/receivables/${id}/baixa`, { settlementTypeId, paidAt }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["receivables"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useGenerateReceivables() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await staffApi.post<{ created: number }>("/receivables/generate")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["receivables"] });
      qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useDeleteReceivable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => staffApi.delete(`/receivables/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["receivables"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
